import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/fp/debounce";

interface ICreateAutoComplete<T = any> {
  data: T | null;
  loading: boolean;
  search: (query: string) => void;
  error: string | null;
}

interface IAutoCompleteContextProviderProps<T> {
  children: ReactNode;
  onSearch: (query: string) => Promise<T>;
  debounceMs?: number;
}

export function createAutoCompleteContext<T>() {
  const Context = createContext<ICreateAutoComplete<T>>({
    data: null,
    loading: false,
    search: () => {},
    error: null,
  });

  function AutoCompleteContextProvider({ onSearch, children, debounceMs = 300 }: IAutoCompleteContextProviderProps<T>) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const componentIsMounted = useRef(true);

    function safeSetState(setStateFn: () => void) {
      if (componentIsMounted.current === false) {
        return;
      }

      setStateFn();
    }

    useEffect(() => {
      return () => {
        componentIsMounted.current = false;
      };
    }, []);

    const performSearch = useCallback(
      debounce(debounceMs, async (query: string) => {
        safeSetState(() => {
          setLoading(true);
        });

        try {
          const data = await onSearch(query);

          safeSetState(() => {
            setData(data);
            setLoading(false);
          });
        } catch (e) {
          safeSetState(() => {
            setError(e.message);
          });
        }
      }),
      [],
    );

    return (
      <Context.Provider
        value={{
          data,
          loading,
          search: (query: string) => {
            performSearch(query);
          },
          error,
        }}
      >
        {children}
      </Context.Provider>
    );
  }

  return {
    Provider: AutoCompleteContextProvider,
    Consumer: Context.Consumer,
    Context,
  };
}
